import { getFunctions, httpsCallable, Functions, HttpsCallable } from "firebase/functions";
import { app } from "./init";

// 初始化 Functions
const functions: Functions = getFunctions(app, "asia-east1");

/**
 * 設置用戶的 admin 權限（更新 Firestore iam 集合）
 */
export async function setAdminClaim(uid: string, isAdmin: boolean): Promise<{ success: boolean; uid: string; iam: { admin: boolean; staff: boolean } }> {
  try {
    const setAdminClaimFn: HttpsCallable<{ uid: string; admin: boolean }, { success: boolean; uid: string; iam: { admin: boolean; staff: boolean } }> = 
      httpsCallable(functions, "setAdminClaim");
    
    const result = await setAdminClaimFn({ uid, admin: isAdmin });
    return result.data;
  } catch (error: any) {
    console.error("Error setting admin claim:", error);
    throw new Error(error.message || "設置 admin 權限失敗");
  }
}

/**
 * 設置用戶的 staff 權限（更新 Firestore iam 集合）
 */
export async function setStaffClaim(uid: string, isStaff: boolean): Promise<{ success: boolean; uid: string; iam: { admin: boolean; staff: boolean; developer?: boolean; observer?: boolean } }> {
  try {
    const setStaffClaimFn: HttpsCallable<{ uid: string; staff: boolean }, { success: boolean; uid: string; iam: { admin: boolean; staff: boolean; developer?: boolean; observer?: boolean } }> = 
      httpsCallable(functions, "setStaffClaim");
    
    const result = await setStaffClaimFn({ uid, staff: isStaff });
    return result.data;
  } catch (error: any) {
    console.error("Error setting staff claim:", error);
    throw new Error(error.message || "設置 staff 權限失敗");
  }
}

/**
 * 設置用戶的 developer 權限（更新 Firestore iam 集合）
 */
export async function setDeveloperClaim(uid: string, isDeveloper: boolean): Promise<{ success: boolean; uid: string; iam: { admin: boolean; staff: boolean; developer: boolean; observer?: boolean } }> {
  try {
    const setDeveloperClaimFn: HttpsCallable<{ uid: string; developer: boolean }, { success: boolean; uid: string; iam: { admin: boolean; staff: boolean; developer: boolean; observer?: boolean } }> = 
      httpsCallable(functions, "setDeveloperClaim");
    
    const result = await setDeveloperClaimFn({ uid, developer: isDeveloper });
    return result.data;
  } catch (error: any) {
    console.error("Error setting developer claim:", error);
    throw new Error(error.message || "設置 developer 權限失敗");
  }
}

/**
 * 設置用戶的 observer 權限（更新 Firestore iam 集合）
 */
export async function setObserverClaim(uid: string, isObserver: boolean): Promise<{ success: boolean; uid: string; iam: { admin: boolean; staff: boolean; developer?: boolean; observer: boolean } }> {
  try {
    const setObserverClaimFn: HttpsCallable<{ uid: string; observer: boolean }, { success: boolean; uid: string; iam: { admin: boolean; staff: boolean; developer?: boolean; observer: boolean } }> = 
      httpsCallable(functions, "setObserverClaim");
    
    const result = await setObserverClaimFn({ uid, observer: isObserver });
    return result.data;
  } catch (error: any) {
    console.error("Error setting observer claim:", error);
    throw new Error(error.message || "設置 observer 權限失敗");
  }
}

/**
 * 通過 email 添加 IAM 用戶
 */
export async function addIAMUser(
  email: string,
  role: "admin" | "staff" | "developer" | "observer"
): Promise<{ success: boolean; uid: string; iam: { admin: boolean; staff: boolean; developer?: boolean; observer?: boolean } }> {
  try {
    const addIAMUserFn: HttpsCallable<
      { email: string; role: "admin" | "staff" | "developer" | "observer" },
      { success: boolean; uid: string; iam: { admin: boolean; staff: boolean; developer?: boolean; observer?: boolean } }
    > = httpsCallable(functions, "addIAMUser");

    const result = await addIAMUserFn({ email, role });
    return result.data;
  } catch (error: any) {
    console.error("Error adding IAM user:", error);
    throw new Error(error.message || "新增 IAM 用戶失敗");
  }
}

/**
 * 更新 Remote Config 參數
 */
export async function updateRemoteConfig(
  parameters: Record<string, string | number | boolean>,
  deleteKeys?: string[]
): Promise<{ success: boolean; message?: string; etag?: string }> {
  try {
    const updateRemoteConfigFn: HttpsCallable<
      { parameters: Record<string, string | number | boolean>; deleteKeys?: string[] },
      { success: boolean; message?: string; etag?: string }
    > = httpsCallable(functions, "updateRemoteConfig");

    const result = await updateRemoteConfigFn({ parameters, deleteKeys });
    return result.data;
  } catch (error: any) {
    console.error("Error updating Remote Config:", error);
    throw new Error(error.message || "更新 Remote Config 失敗");
  }
}

/**
 * 發布 Remote Config 變更
 */
export async function publishRemoteConfig(): Promise<{ success: boolean; message?: string; etag?: string }> {
  try {
    const publishRemoteConfigFn: HttpsCallable<
      void,
      { success: boolean; message?: string; etag?: string }
    > = httpsCallable(functions, "publishRemoteConfig");

    const result = await publishRemoteConfigFn();
    return result.data;
  } catch (error: any) {
    console.error("Error publishing Remote Config:", error);
    throw new Error(error.message || "發布 Remote Config 失敗");
  }
}

